import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Plus, Trash, MagnifyingGlass, FilePdf, FileXls } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

export function TicketsSurveyPanel() {
  const { token } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const handleExport = async (format) => {
    try {
      const res = await axios.get(`${API}/surveys/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const ext = format === "excel" ? "xlsx" : "pdf";
      const mime = format === "excel" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `encuestas_satisfaccion.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado a ${ext.toUpperCase()}`);
    } catch { toast.error("Error al exportar"); }
  };

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [fbRes, cfgRes] = await Promise.all([
        axios.get(`${API}/tickets/all-feedback`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/tickets/survey-config`, { headers }),
      ]);
      setFeedbacks(fbRes.data || []);
      setQuestions(cfgRes.data?.questions || []);
    } catch (e) {
      process.env.NODE_ENV === "development" && console.error(e);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/tickets/survey-config`, { questions }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Preguntas guardadas");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const addQuestion = (type) => {
    setQuestions([...questions, { id: `q${Date.now()}`, text: "", type }]);
  };
  const removeQuestion = (idx) => setQuestions(questions.filter((_, i) => i !== idx));
  const updateQuestion = (idx, text) => {
    const nq = [...questions];
    nq[idx] = { ...nq[idx], text };
    setQuestions(nq);
  };

  const filtered = feedbacks.filter(f => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (f.client_name || "").toLowerCase().includes(s)
      || (f.company || "").toLowerCase().includes(s)
      || (f.ticket_number || "").toLowerCase().includes(s);
  });

  // KPIs
  const total = feedbacks.length;
  const resolved = feedbacks.filter(f => f.resolved).length;
  const notResolved = total - resolved;
  const avgRating = total > 0 ? (feedbacks.reduce((s, f) => s + (f.rating || 0), 0) / feedbacks.filter(f => f.rating).length || 0).toFixed(1) : "0";
  const resolvedPct = total > 0 ? Math.round((resolved / total) * 100) : 0;

  const fmtDate = (d) => { try { return format(new Date(d), "dd MMM yyyy", { locale: es }); } catch { return ""; } };

  const StarDisplay = ({ rating: r }) => (
    <div className="flex items-center gap-0.5 justify-center">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={14} weight={s <= r ? "fill" : "regular"} className={s <= r ? "text-amber-400" : "text-slate-300"} />
      ))}
    </div>
  );

  return (
    <div data-testid="surveys-panel">
      {/* Title + Export buttons */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-slate-900">Encuestas de Satisfaccion</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={() => handleExport("excel")} data-testid="export-surveys-excel">
            <FileXls size={16} className="text-emerald-600" /> Excel
          </Button>
          <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5" onClick={() => handleExport("pdf")} data-testid="export-surveys-pdf">
            <FilePdf size={16} className="text-red-500" /> PDF
          </Button>
        </div>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-xl p-5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Respuestas</p>
          <p className="text-3xl font-extrabold text-slate-900 mt-1">{total}</p>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Satisfaccion Promedio</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-3xl font-extrabold text-amber-500">{avgRating}</p>
            <StarDisplay rating={Math.round(parseFloat(avgRating))} />
          </div>
          <p className="text-xs text-slate-500 mt-1">de 5.0</p>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Resueltos</p>
          <p className="text-3xl font-extrabold text-emerald-600 mt-1">{resolvedPct}%</p>
          <p className="text-xs text-slate-500 mt-1">{resolved} de {total}</p>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">No Resueltos</p>
          <p className="text-3xl font-extrabold text-red-500 mt-1">{total > 0 ? 100 - resolvedPct : 0}%</p>
          <p className="text-xs text-slate-500 mt-1">{notResolved} de {total}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Table */}
        <div className="lg:col-span-2">
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-800">Respuestas de Clientes</h2>
              <div className="relative">
                <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por cliente o ticket..."
                  className="pl-8 w-64 h-8 text-xs" data-testid="survey-search" />
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                {total === 0 ? "No hay encuestas respondidas aun" : "Sin resultados para la busqueda"}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="feedback-table">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase text-slate-400 tracking-wider border-b bg-slate-50">
                      <th className="text-left py-3 px-4">Ticket</th>
                      <th className="text-left py-3 px-4">Cliente / Empresa</th>
                      <th className="text-center py-3 px-4">Resuelto</th>
                      <th className="text-center py-3 px-4">Calificacion</th>
                      <th className="text-left py-3 px-4">Comentario</th>
                      <th className="text-left py-3 px-4">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(f => (
                      <tr key={f.id} className={`border-b hover:bg-blue-50/50 ${!f.resolved ? "bg-red-50/30" : ""}`} data-testid={`feedback-row-${f.id}`}>
                        <td className="py-3 px-4">
                          <span className="text-blue-700 font-bold text-xs">{f.ticket_number || "—"}</span>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-semibold text-slate-800">{f.client_name || "—"}</p>
                          {f.company && <p className="text-[10px] text-slate-400">{f.company}</p>}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${f.resolved ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                            {f.resolved ? "Si" : "No"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <StarDisplay rating={f.rating || 0} />
                          <span className="text-[10px] text-slate-400">{f.rating || 0}/5</span>
                        </td>
                        <td className="py-3 px-4 max-w-[200px]">
                          <p className={`text-xs truncate ${!f.resolved ? "text-red-600" : "text-slate-600"}`}>{f.comment || "—"}</p>
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-400">{fmtDate(f.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: Config */}
        <div className="lg:col-span-1">
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b bg-slate-50">
              <h2 className="text-sm font-bold text-slate-800">Configurar Preguntas</h2>
              <p className="text-[10px] text-slate-400 mt-0.5">Modifica las preguntas de la encuesta</p>
            </div>
            <div className="p-5 space-y-4">
              {questions.map((q, idx) => (
                <div key={q.id || `q-${idx}`} className="bg-slate-50 border rounded-lg p-3" data-testid={`question-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold uppercase ${q.type === "rating" ? "text-emerald-600" : "text-blue-600"}`}>
                      Pregunta {idx + 1} · {q.type === "rating" ? "Calificacion" : "Si/No"}
                    </span>
                    <button onClick={() => removeQuestion(idx)} className="text-red-400 hover:text-red-600 text-xs font-medium" data-testid={`remove-question-${idx}`}>
                      <Trash size={14} />
                    </button>
                  </div>
                  <Input value={q.text} onChange={(e) => updateQuestion(idx, e.target.value)}
                    className="text-sm" placeholder="Escribe la pregunta..." data-testid={`question-text-${idx}`} />
                </div>
              ))}

              <div className="flex gap-2">
                <button onClick={() => addQuestion("yesno")} className="flex-1 py-2 border-2 border-dashed border-slate-300 rounded-lg text-xs font-semibold text-slate-500 hover:border-blue-400 hover:text-blue-600 transition-colors" data-testid="add-yesno-question">
                  + Si/No
                </button>
                <button onClick={() => addQuestion("rating")} className="flex-1 py-2 border-2 border-dashed border-slate-300 rounded-lg text-xs font-semibold text-slate-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors" data-testid="add-rating-question">
                  + Calificacion
                </button>
              </div>

              <Button onClick={handleSave} disabled={saving} className="w-full btn-primary" data-testid="save-survey-config">
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
